import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import clsx from 'clsx';

import AuthCheck from '@app/components/AuthCheck';
import Link from '@app/components/Link';
import PageTitle from '@app/components/PageTitle';
import {
  Skeleton,
  SubHeader,
  SubHeaderMenu,
  SubHeaderTitle,
} from '@app/components/Skeleton/Skeleton';
import { isTruthy } from '@app/helpers/predicate';
import {
  shouldHaveSimpleHeader,
  shouldShowStepper,
} from '@app/helpers/simpleHeader';

import { Stepper } from '../../SubscriptionCheckout/components/Stepper';
import useGetSubscriptionIntent from '../../SubscriptionCheckout/hooks/useGetSubscriptionIntent/useGetSubscriptionIntent';
import SubscriptionErrorBoundary from '../SubscriptionErrorBoundary';
import messages from './SubscriptionLayout.messages';

// Duplicating as temporary solution until we unify them on NI v2
// Needed for having a specific Error Boundary for Subscription Checkout Launch.
const SubscriptionLayout = ({ children }: { children?: React.ReactNode }) => {
  const { language } = useParams<{ language: string }>();
  const { pathname } = useLocation();
  const { subscriptionIntentId } = useParams<{
    subscriptionIntentId: string;
  }>();

  const { isSubscriptionAnUpdate } =
    useGetSubscriptionIntent(subscriptionIntentId);

  const simpleHeader = shouldHaveSimpleHeader(pathname, isSubscriptionAnUpdate);

  return (
    <AuthCheck>
      <Skeleton simpleHeader={simpleHeader}>
        <SubscriptionErrorBoundary>
          {!isTruthy(simpleHeader) && (
            <>
              <PageTitle>{messages.title}</PageTitle>

              <SubHeader>
                <SubHeaderTitle>
                  <a href={`/${language}/account/`}>
                    <FormattedMessage {...messages.title} />
                  </a>
                </SubHeaderTitle>

                <SubHeaderMenu>
                  <a href={`/${language}/my-account/account-settings/`}>
                    <FormattedMessage {...messages.accountSettings} />
                  </a>
                  <Link to="/:language/account/orders/">
                    <FormattedMessage {...messages.orderHistory} />
                  </Link>
                  <a
                    href={`/${language}/my-account/my-products-serials-downloads/`}
                  >
                    <FormattedMessage
                      {...messages.productsSerialsAndDownloads}
                    />
                  </a>
                  <a href={`/${language}/support/downloads/`}>
                    <FormattedMessage {...messages.updatesAndDrivers} />
                  </a>
                  <Link to="/:language/account/subscription/">
                    <FormattedMessage {...messages.subscription} />
                  </Link>
                  <a href={`/${language}/my-account/license-transfer/`}>
                    <FormattedMessage {...messages.licenseTransfer} />
                  </a>
                </SubHeaderMenu>
              </SubHeader>
            </>
          )}

          <div className="ni-tailwind-namespace">
            {isTruthy(shouldShowStepper(pathname, isSubscriptionAnUpdate)) && (
              <div
                className={clsx(
                  'justify-center container content-start grow auto-rows-min',
                  'grid-cols-4 webex-grid lg:grid-cols-8 xl:grid-cols-12',
                )}
              >
                <Stepper />
              </div>
            )}
            <div
              className={clsx(
                'justify-center container content-start grow auto-rows-min mt-6 mb-12 xl:mb-18',
                'grid-cols-4 webex-grid lg:grid-cols-8 xl:grid-cols-12',
              )}
            >
              {children}
            </div>
          </div>
        </SubscriptionErrorBoundary>
      </Skeleton>
    </AuthCheck>
  );
};

export default SubscriptionLayout;
